import React, { useEffect, useState } from "react";
import { useNavigate, useParams, useSearchParams } from "react-router-dom";
import { apiClient, formatApiErrorDetail } from "../lib/api";
import { AppHeader } from "../components/AppHeader";
import { Button } from "../components/ui/button";
import { Skeleton } from "../components/ui/skeleton";
import {
  ArrowLeft, Trophy, Sparkles, ShoppingBag, CheckCircle2, ArrowRight,
} from "lucide-react";
import { toast } from "sonner";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle, AlertDialogTrigger,
} from "../components/ui/alert-dialog";

export default function BulkComparison() {
  const navigate = useNavigate();
  const { id } = useParams();
  const [params] = useSearchParams();
  const customerId = params.get("customer_id") || null;

  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [selections, setSelections] = useState({}); // index -> supplier_id
  const [placing, setPlacing] = useState(false);

  const refresh = () => {
    apiClient.get(`/bulk-price-requests/${id}`)
      .then((r) => {
        setData(r.data);
        // Pre-select cheapest by default for items not yet ordered
        const sel = {};
        r.data.items.forEach((it) => {
          if (!it.order_id) sel[it.index] = it.best_supplier_id;
        });
        setSelections(sel);
      })
      .catch(() => toast.error("Not found"))
      .finally(() => setLoading(false));
  };
  useEffect(refresh, [id]);

  if (loading) {
    return (
      <>
        <AppHeader title="Comparison" />
        <div className="px-4 pt-4 space-y-2">{[1, 2, 3].map((i) => <Skeleton key={i} className="h-32 rounded-2xl" />)}</div>
      </>
    );
  }
  if (!data) return null;

  const supplierIds = data.supplier_ids;
  const pendingItems = data.items.filter((it) => !it.order_id);

  // Aggregate totals per supplier (over pending items)
  const totals = {};
  for (const sid of supplierIds) {
    let total = 0;
    let complete = true;
    for (const it of pendingItems) {
      const q = it.quotes.find((x) => x.supplier_id === sid);
      if (q) total += q.price;
      else complete = false;
    }
    totals[sid] = { total, complete };
  }
  const cheapestSupplier = supplierIds.reduce((best, sid) => {
    if (best === null) return sid;
    return totals[sid].total < totals[best].total ? sid : best;
  }, supplierIds.length > 0 ? supplierIds[0] : null);

  // Sum of "best per item" (the optimal mix-and-match)
  const optimalTotal = pendingItems.reduce((sum, it) => {
    const sel = selections[it.index] || it.best_supplier_id;
    const q = it.quotes.find((x) => x.supplier_id === sel);
    return sum + (q?.price || 0);
  }, 0);

  const placeBulk = async (allCheapest) => {
    setPlacing(true);
    try {
      const payload = allCheapest
        ? { bulk_request_id: data.id, all_cheapest: true, customer_id: customerId }
        : {
            bulk_request_id: data.id,
            selections: pendingItems.map((it) => ({ index: it.index, supplier_id: selections[it.index] || it.best_supplier_id })),
            customer_id: customerId,
          };
      const { data: res } = await apiClient.post("/bulk-orders", payload);
      toast.success(`${res.created} orders placed`);
      refresh();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Failed");
    } finally { setPlacing(false); }
  };

  const placeOne = async (item) => {
    const sid = selections[item.index] || item.best_supplier_id;
    setPlacing(true);
    try {
      const { data: res } = await apiClient.post("/bulk-orders", {
        bulk_request_id: data.id,
        selections: [{ index: item.index, supplier_id: sid }],
        customer_id: customerId,
      });
      if (res.created > 0) toast.success("Order placed");
      refresh();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Failed");
    } finally { setPlacing(false); }
  };

  return (
    <>
      <AppHeader title="Comparison" subtitle={`${data.items.length} items × ${supplierIds.length} suppliers`} />
      <div className="px-4 pt-4 pb-28 space-y-4">
        <button onClick={() => navigate(-1)} data-testid="bulk-cmp-back" className="flex items-center gap-1 text-sm font-semibold text-slate-600">
          <ArrowLeft className="w-4 h-4" /> Back
        </button>

        <div className="bg-emerald-50 border border-emerald-200 rounded-2xl p-4">
          <div className="flex items-start gap-3">
            <Sparkles className="w-6 h-6 text-emerald-700 mt-0.5" />
            <div className="text-sm">
              <div className="font-bold text-emerald-900">Quotes received from {supplierIds.length} suppliers</div>
              <div className="text-emerald-800 text-xs mt-1">Pick the best supplier per row, or place all with cheapest in one tap.</div>
            </div>
          </div>
        </div>

        {/* Per-supplier totals */}
        <div className="bg-white border border-slate-200 rounded-2xl p-4 space-y-2" data-testid="totals-card">
          <h4 className="text-xs uppercase tracking-wider text-slate-500 font-bold">Total if you order ALL pending items from one supplier</h4>
          {pendingItems.length === 0 ? (
            <p className="text-sm text-slate-500">All items already ordered.</p>
          ) : (
            supplierIds.map((sid) => {
              const isBest = sid === cheapestSupplier;
              const t = totals[sid];
              return (
                <div key={sid} data-testid={`total-${sid}`}
                  className={`flex items-center justify-between p-2 rounded-lg ${isBest ? "bg-emerald-50 border border-emerald-200" : ""}`}>
                  <div className="flex items-center gap-2 min-w-0">
                    <span className="font-semibold text-slate-900 truncate">{data.supplier_names[sid]}</span>
                    {isBest && <span className="inline-flex items-center gap-1 text-[10px] font-bold uppercase tracking-wider bg-emerald-600 text-white px-1.5 py-0.5 rounded-full"><Trophy className="w-3 h-3" />Best</span>}
                  </div>
                  <div className="font-bold text-slate-900">₹{t.total.toFixed(2)}</div>
                </div>
              );
            })
          )}
          {pendingItems.length > 0 && (
            <div className="flex items-center justify-between p-2 mt-2 border-t border-dashed border-slate-200">
              <span className="text-sm font-bold text-emerald-700">Optimal mix (best per item)</span>
              <span className="text-base font-bold text-emerald-700" data-testid="optimal-total">₹{optimalTotal.toFixed(2)}</span>
            </div>
          )}
        </div>

        {/* Quick actions */}
        {pendingItems.length > 0 && (
          <div className="grid grid-cols-2 gap-2">
            <AlertDialog>
              <AlertDialogTrigger asChild>
                <Button data-testid="place-all-cheapest-btn" disabled={placing}
                  className="h-14 rounded-xl bg-emerald-600 hover:bg-emerald-700 text-white font-semibold gap-2">
                  <Trophy className="w-4 h-4" /> All cheapest
                </Button>
              </AlertDialogTrigger>
              <AlertDialogContent className="max-w-sm rounded-2xl">
                <AlertDialogHeader>
                  <AlertDialogTitle>Place {pendingItems.length} orders with cheapest supplier per item?</AlertDialogTitle>
                  <AlertDialogDescription>This will create one order per pending item using the lowest-priced supplier.</AlertDialogDescription>
                </AlertDialogHeader>
                <AlertDialogFooter>
                  <AlertDialogCancel className="rounded-xl">Cancel</AlertDialogCancel>
                  <AlertDialogAction onClick={() => placeBulk(true)} data-testid="confirm-all-cheapest" className="bg-emerald-600 hover:bg-emerald-700 rounded-xl">Confirm</AlertDialogAction>
                </AlertDialogFooter>
              </AlertDialogContent>
            </AlertDialog>

            <Button onClick={() => placeBulk(false)} disabled={placing} data-testid="place-all-selected-btn"
              variant="outline" className="h-14 rounded-xl border-emerald-300 text-emerald-700 hover:bg-emerald-50 font-semibold gap-2">
              <ShoppingBag className="w-4 h-4" /> Place selected
            </Button>
          </div>
        )}

        {/* Items list */}
        <div className="space-y-3" data-testid="bulk-items-comparison">
          {data.items.map((it) => (
            <div key={it.index} data-testid={`bulk-cmp-item-${it.index}`}
              className={`bg-white rounded-2xl p-4 border ${it.order_id ? "border-emerald-200 bg-emerald-50/30" : "border-slate-200"}`}>
              <div className="flex items-start justify-between gap-3 mb-3">
                <div className="min-w-0">
                  <div className="font-bold text-slate-900 truncate">{it.product}</div>
                  <div className="text-xs text-slate-500">Qty: {it.quantity} {it.unit}</div>
                </div>
                {it.order_id && (
                  <span className="inline-flex items-center gap-1 text-[10px] font-bold uppercase tracking-wider bg-emerald-600 text-white px-2 py-0.5 rounded-full">
                    <CheckCircle2 className="w-3 h-3" /> Ordered
                  </span>
                )}
              </div>

              <div className="space-y-1.5">
                {[...it.quotes].sort((a, b) => a.price - b.price).map((q) => {
                  const isBest = q.supplier_id === it.best_supplier_id;
                  const isSelected = (selections[it.index] || it.best_supplier_id) === q.supplier_id;
                  const isOrdered = it.ordered_supplier_id === q.supplier_id;
                  return (
                    <button
                      key={q.supplier_id}
                      data-testid={`bulk-quote-${it.index}-${q.supplier_id}`}
                      disabled={!!it.order_id}
                      onClick={() => setSelections((s) => ({ ...s, [it.index]: q.supplier_id }))}
                      className={`w-full text-left p-2.5 rounded-lg border-2 transition flex items-center justify-between ${
                        isOrdered ? "bg-emerald-100 border-emerald-400"
                          : isSelected && !it.order_id ? "border-emerald-600 bg-emerald-50"
                          : "border-slate-200 bg-white"
                      } ${it.order_id ? "opacity-70" : ""}`}
                    >
                      <div className="flex items-center gap-2 min-w-0">
                        <span className="font-semibold text-slate-900 truncate text-sm">{q.supplier_name}</span>
                        {isBest && <Trophy className="w-3.5 h-3.5 text-emerald-600 shrink-0" />}
                      </div>
                      <div className="font-bold text-slate-900 text-sm">₹{q.price.toFixed(2)}</div>
                    </button>
                  );
                })}
              </div>

              {!it.order_id && (
                <Button onClick={() => placeOne(it)} disabled={placing} data-testid={`bulk-place-row-${it.index}`}
                  size="sm" className="w-full mt-3 h-10 bg-emerald-600 hover:bg-emerald-700 text-white rounded-lg font-semibold gap-1">
                  Add to {data.supplier_names[selections[it.index] || it.best_supplier_id]} order <ArrowRight className="w-3.5 h-3.5" />
                </Button>
              )}
            </div>
          ))}
        </div>

        {pendingItems.length === 0 && (
          <Button onClick={() => navigate("/orders")} data-testid="goto-orders-btn"
            className="w-full h-14 bg-slate-900 hover:bg-slate-800 text-white rounded-xl text-lg font-semibold gap-2">
            <ShoppingBag className="w-5 h-5" /> View All Orders
          </Button>
        )}
      </div>
    </>
  );
}
